import { Box, Typography, styled } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';

import ChartCard from 'components/charts/chart-card';
import SparklineChart from 'components/charts/sparkline-chart';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import DataLabDataGrid from 'views/playground/features/data-lab/components/data-lab-data-grid';
import MovementSankeyChart from 'views/playground/features/data-lab/components/charts/movement-sankey-chart';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import { MOTION_TOKENS } from 'views/playground/features/data-lab/constants/motion-tokens';
import {
  TransferSummaryRow,
  deriveFlowSmallMultiples,
  deriveTransferSummary,
} from 'views/playground/features/data-lab/utils/derive-chart-data';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

// Derived once at module scope: it depends only on the fixture, never on the
// row-selection state below.
const flowSmallMultiples = deriveFlowSmallMultiples();

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
}));

const PageTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const TableAndSankeyRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr',
  gap: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const SmallMultiplesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  // Use a narrower minimum item width so this section fills available space
  // beside the transfer table.
  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
  gap: theme.spacing(3),
}));

// The pointer cursor marks the rows as clickable before the user reads the
// help text below the heading.
const ClickableDataGrid = styled(DataLabDataGrid)<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
  },
  // An inset accent marks selection without shifting cell content. Hover
  // changes the row background, while selection remains visible on top.
  '& .MuiDataGrid-row.selected': {
    boxShadow: `inset 2px 0 0 0 ${chartPalette.series.categorical[0]}`,
    backgroundColor: chartPalette.backgrounds.sunken,
  },
}));

const SmallMultipleCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHighlighted' && prop !== 'isDimmed' && prop !== 'chartPalette',
})<{
  isHighlighted: boolean;
  isDimmed: boolean;
  chartPalette: ChartPalette;
}>(({ theme, isHighlighted, isDimmed, chartPalette }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: 8,
  border: `1px solid ${isHighlighted ? chartPalette.series.categorical[0] : chartPalette.borders.subtle}`,
  backgroundColor: chartPalette.backgrounds.sunken,
  opacity: isDimmed ? 0.4 : 1,
  transition: `opacity ${MOTION_TOKENS.fade.duration}ms ${MOTION_TOKENS.fade.easing}, border-color ${MOTION_TOKENS.fade.duration}ms ${MOTION_TOKENS.fade.easing}`,
}));

const SmallMultipleLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: chartPalette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const SmallMultipleFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const SmallMultipleValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  fontSize: 14,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: chartPalette.text.primary,
}));

// The Sankey is Pro-only, so this tab presents the transfer data as small
// multiples (one
// sparkline per cage, "what happened here") and a transfer table (the exact
// rows a Sankey's ribbons would encode). Selecting a transfer row highlights
// the two sparklines it touches.
export const FlowScreenBody: React.FC = () => {
  const { formatCount, formatKg } = useMetricFormatters();
  const chartPalette = useChartPalette();

  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);

  const transferRows = useMemo(() => deriveTransferSummary('Stocking'), []);

  const selectedTransfer = useMemo(
    () => transferRows.find((row) => row.id === selectedTransferId) ?? null,
    [transferRows, selectedTransferId]
  );

  const highlightedCageIds = useMemo(() => {
    if (!selectedTransfer) return [];
    return [selectedTransfer.originCageId, selectedTransfer.destinationCageId].filter((id): id is string => id !== null);
  }, [selectedTransfer]);

  const columns: GridColDef<TransferSummaryRow>[] = useMemo(
    () => [
      { field: 'fishGroupName', headerName: 'Fish group', flex: 1 },
      { field: 'originLabel', headerName: 'Origin', flex: 1 },
      { field: 'destinationLabel', headerName: 'Destination', flex: 1 },
      {
        field: 'week',
        headerName: 'Week',
        flex: 0.6,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'biomassKg',
        headerName: 'Biomass moved',
        flex: 1,
        renderCell: (params) => formatKg(params.row.biomassKg),
      },
    ],
    [formatKg]
  );

  return (
    <>
      <HeaderRow>
        <PageTitle chartPalette={chartPalette} variant="h2">
          Flow
        </PageTitle>
      </HeaderRow>

      <Row>
        <ChartCard title="Population by cage" subtitle="Sorted by biggest change">
          <SmallMultiplesGrid>
            {flowSmallMultiples.map((item) => {
              const isHighlighted = highlightedCageIds.includes(item.cageId);
              const isDimmed = highlightedCageIds.length > 0 && !isHighlighted;

              return (
                <SmallMultipleCard key={item.cageId} chartPalette={chartPalette} isHighlighted={isHighlighted} isDimmed={isDimmed}>
                  <SmallMultipleLabel chartPalette={chartPalette} title={item.label}>
                    {item.label}
                  </SmallMultipleLabel>
                  <SmallMultipleFooter>
                    <SparklineChart data={item.series} height={28} disableKeyboardNavigation />
                    <SmallMultipleValue chartPalette={chartPalette}>{formatCount(item.endValue)}</SmallMultipleValue>
                  </SmallMultipleFooter>
                </SmallMultipleCard>
              );
            })}
          </SmallMultiplesGrid>
        </ChartCard>

        <TableAndSankeyRow>
          <ChartCard
            title="Fish group transfers"
            subtitle="Click a row to highlight its origin and destination cages in Population by cage."
          >
            <ClickableDataGrid
              chartPalette={chartPalette}
              autoHeight
              hideFooter
              rows={transferRows}
              columns={columns}
              // Keep parity classes while adding the selected state so row
              // striping remains visible.
              getRowClassName={(params) => {
                const parity = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
                return String(params.id) === selectedTransferId ? `${parity} selected` : parity;
              }}
              onRowClick={(params) => setSelectedTransferId((current) => (current === String(params.id) ? null : String(params.id)))}
              // Enter and Space provide the same selection behavior as a
              // pointer click.
              onCellKeyDown={(params, event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                setSelectedTransferId((current) => (current === String(params.id) ? null : String(params.id)));
              }}
            />
          </ChartCard>

          <ChartCard title="Fish movement">
            <MovementSankeyChart />
          </ChartCard>
        </TableAndSankeyRow>
      </Row>
    </>
  );
};

const FlowScreen: React.FC = () => (
  <DataLab>
    <FlowScreenBody />
  </DataLab>
);

export default FlowScreen;
